import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wallet, CheckCircle2, XCircle, Banknote, ChevronDown, ChevronUp, Pencil, Eye, ShieldAlert, AlertCircle, Pause, RotateCw, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../lib/api';

/**
 * Admin-only — review all organiser payout requests across the platform.
 * Shows the full payout breakdown (gross / fees / refunds / chargebacks /
 * reserve holds / net), per-event audit trail, and adjustments editor.
 */
const STATUS_TONE = {
  pending:                'bg-amber-50 text-amber-800 border-amber-200',
  approved:               'bg-blue-50 text-blue-800 border-blue-200',
  paid:                   'bg-emerald-50 text-emerald-800 border-emerald-200',
  rejected:               'bg-rose-50 text-rose-800 border-rose-200',
  needs_more_information: 'bg-orange-50 text-orange-800 border-orange-200',
  on_hold:                'bg-stone-100 text-stone-700 border-stone-200',
};
const STATUS_LABEL = {
  pending: 'Pending Review',
  approved: 'Approved',
  paid: 'Paid',
  rejected: 'Rejected',
  needs_more_information: 'Needs Info',
  on_hold: 'On Hold',
};

const Row = ({ label, value, emphasis = false, negative = false }) => (
  <div className="flex justify-between text-xs">
    <span className="text-stone-600">{label}</span>
    <span className={`${emphasis ? 'font-semibold text-[#1F3D2B]' : 'text-stone-700'} ${negative ? '' : ''}`}>
      {negative && value !== 0 ? '−' : ''}R{Math.abs(value).toFixed(2)}
    </span>
  </div>
);

const PayoutCard = ({ payout, onDecide, onAdjust, onAction, onUploadProof, onRevealBanking }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [cb, setCb] = useState(String(payout.chargebacks ?? 0));
  const [rh, setRh] = useState(String(payout.reserve_holds ?? 0));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  // Phase 3 dialogs
  const [actionDialog, setActionDialog] = useState(null); // { action, label }
  const [actionText, setActionText] = useState('');
  const [paidRef, setPaidRef] = useState('');
  const [paidDate, setPaidDate] = useState(new Date().toISOString().slice(0, 10));
  const [paidProofFile, setPaidProofFile] = useState(null); // optional File for mark-paid
  const [actionSaving, setActionSaving] = useState(false);

  // Banking reveal
  const [revealConfirm, setRevealConfirm] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [revealed, setRevealed] = useState(null);

  const totals = payout.breakdown_totals || {};
  const rows = payout.event_breakdown || [];
  const editable = payout.status === 'pending' || payout.status === 'approved';

  const openActionDialog = (action, label) => {
    setActionDialog({ action, label });
    setActionText('');
    setPaidRef('');
    setPaidDate(new Date().toISOString().slice(0, 10));
    setPaidProofFile(null);
  };

  // Read a File as a base64 data URL (used for proof-of-payment upload)
  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const submitAction = async () => {
    if (!actionDialog) return;
    setActionSaving(true);
    try {
      const body = {};
      if (actionDialog.action === 'reject') body.rejection_reason = actionText;
      if (actionDialog.action === 'needs-more-info') body.info_request = actionText;
      if (actionDialog.action === 'on-hold') body.hold_reason = actionText;
      if (actionDialog.action === 'mark-paid') {
        if (!paidRef.trim()) {
          toast.error('Please enter the bank payment reference.');
          setActionSaving(false);
          return;
        }
        body.manual_payment_reference = paidRef.trim();
        body.manual_payment_date = paidDate;
      }
      await onAction(payout.payout_id, actionDialog.action, body);
      // If admin attached a proof during mark-paid, upload it right after.
      if (actionDialog.action === 'mark-paid' && paidProofFile) {
        try {
          const dataUrl = await readFileAsDataUrl(paidProofFile);
          await onUploadProof(payout.payout_id, dataUrl, paidProofFile.name);
          toast.success('Proof of payment uploaded.');
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Proof upload failed — you can retry from the row.');
        }
      }
      setActionDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed.');
    } finally {
      setActionSaving(false);
    }
  };

  const reveal = async () => {
    setRevealing(true);
    try {
      const data = await onRevealBanking(payout.payout_id);
      setRevealed(data);
      setRevealConfirm(false);
      toast.success('Banking details revealed (audit-logged).');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reveal banking details.');
    } finally {
      setRevealing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await onAdjust(payout.payout_id, {
        chargebacks: parseFloat(cb) || 0,
        reserve_holds: parseFloat(rh) || 0,
        note: note || null,
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  // Determine which action buttons to show for the current status
  const canApprove = payout.status === 'pending';
  const canReject = payout.status === 'pending';
  const canMarkPaid = payout.status === 'approved';
  const canNeedsInfo = ['pending', 'on_hold'].includes(payout.status);
  const canOnHold = ['pending', 'needs_more_information'].includes(payout.status);
  const canResume = ['needs_more_information', 'on_hold'].includes(payout.status);

  return (
    <div className="p-5" data-testid={`admin-payout-${payout.status}`}>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <span className={`px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider rounded-full border ${STATUS_TONE[payout.status] || 'bg-stone-100 text-stone-700 border-stone-200'}`}>
              {STATUS_LABEL[payout.status] || payout.status}
            </span>
            <p className="font-serif text-xl text-[#1F3D2B]" data-testid={`payout-amount-${payout.payout_id}`}>
              R{payout.amount.toFixed(2)}
            </p>
          </div>
          <p className="text-sm text-stone-700">{payout.owner_name} · {payout.owner_email}</p>
          <p className="text-xs text-stone-500 mt-1 flex items-center gap-1.5">
            <Banknote className="w-3.5 h-3.5" />
            {payout.bank_snapshot?.bank_name} · {payout.bank_snapshot?.account_holder} · {payout.bank_snapshot?.account_number_masked}
            <button
              type="button"
              onClick={() => setRevealConfirm(true)}
              className="ml-1 text-[10px] text-rose-700 underline hover:no-underline inline-flex items-center gap-0.5"
              data-testid={`reveal-banking-${payout.payout_id}`}
            >
              <Eye className="w-3 h-3" /> Reveal
            </button>
          </p>
          {payout.notes && <p className="text-xs italic text-stone-500 mt-1">&quot;{payout.notes}&quot;</p>}
          {payout.rejection_reason && (
            <p className="text-xs text-rose-700 mt-1"><strong>Rejected:</strong> {payout.rejection_reason}</p>
          )}
          {payout.info_request && (
            <p className="text-xs text-orange-700 mt-1"><strong>Info requested:</strong> {payout.info_request}</p>
          )}
          {payout.hold_reason && (
            <p className="text-xs text-stone-700 mt-1"><strong>On hold:</strong> {payout.hold_reason}</p>
          )}
          {payout.manual_payment_reference && (
            <p className="text-xs text-emerald-700 mt-1">
              <strong>Paid:</strong> ref {payout.manual_payment_reference} · {(payout.manual_payment_date || '').slice(0, 10)}
              {payout.proof_of_payment_url && (
                <a href={payout.proof_of_payment_url} target="_blank" rel="noreferrer" className="ml-2 underline">View proof</a>
              )}
            </p>
          )}
          <p className="text-xs text-stone-400 mt-2">
            Requested {new Date(payout.requested_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap flex-shrink-0">
          {canApprove && (
            <Button size="sm" onClick={() => onDecide(payout.payout_id, 'approve')} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white" data-testid={`admin-approve-${payout.payout_id}`}>
              <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
            </Button>
          )}
          {canReject && (
            <Button size="sm" variant="outline" onClick={() => openActionDialog('reject', 'Reject payout')} className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`admin-reject-${payout.payout_id}`}>
              <XCircle className="w-4 h-4 mr-1" /> Reject
            </Button>
          )}
          {canMarkPaid && (
            <Button size="sm" onClick={() => openActionDialog('mark-paid', 'Mark as paid')} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`admin-mark-paid-${payout.payout_id}`}>
              <CheckCircle2 className="w-4 h-4 mr-1" /> Mark as paid
            </Button>
          )}
          {canNeedsInfo && (
            <Button size="sm" variant="outline" onClick={() => openActionDialog('needs-more-info', 'Request more information')} className="rounded-full border-orange-300 text-orange-700 hover:bg-orange-50" data-testid={`admin-needs-info-${payout.payout_id}`}>
              <AlertCircle className="w-4 h-4 mr-1" /> Needs info
            </Button>
          )}
          {canOnHold && (
            <Button size="sm" variant="outline" onClick={() => openActionDialog('on-hold', 'Place on hold')} className="rounded-full border-stone-400 text-stone-700 hover:bg-stone-100" data-testid={`admin-on-hold-${payout.payout_id}`}>
              <Pause className="w-4 h-4 mr-1" /> On hold
            </Button>
          )}
          {canResume && (
            <Button size="sm" variant="outline" onClick={() => onDecide(payout.payout_id, 'resume')} className="rounded-full border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`admin-resume-${payout.payout_id}`}>
              <RotateCw className="w-4 h-4 mr-1" /> Resume
            </Button>
          )}
          {payout.status === 'paid' && !payout.proof_of_payment_url && (
            <label className="inline-flex items-center cursor-pointer text-xs text-[#1F3D2B] underline hover:no-underline" data-testid={`upload-proof-label-${payout.payout_id}`}>
              <Upload className="w-3.5 h-3.5 mr-1" /> Upload proof
              <input
                type="file"
                accept="image/png,image/jpeg,application/pdf"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = async () => {
                    try {
                      await onUploadProof(payout.payout_id, reader.result, file.name);
                      toast.success('Proof uploaded.');
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Upload failed.');
                    }
                  };
                  reader.readAsDataURL(file);
                }}
              />
            </label>
          )}
        </div>
      </div>

      {/* Action dialog (reject / mark-paid / needs-info / on-hold) */}
      <Dialog open={!!actionDialog} onOpenChange={(o) => !o && setActionDialog(null)}>
        <DialogContent data-testid={`action-dialog-${payout.payout_id}`}>
          <DialogHeader>
            <DialogTitle>{actionDialog?.label}</DialogTitle>
            <DialogDescription>
              {actionDialog?.action === 'mark-paid'
                ? 'The organiser will be notified by email with the payment reference.'
                : 'The organiser will be notified by email.'}
            </DialogDescription>
          </DialogHeader>
          {actionDialog?.action === 'mark-paid' ? (
            <div className="space-y-3 mt-2">
              <div>
                <Label>Bank payment reference *</Label>
                <Input value={paidRef} onChange={(e) => setPaidRef(e.target.value)} placeholder="EFT12345 / FNB ref" data-testid={`paid-ref-${payout.payout_id}`} />
              </div>
              <div>
                <Label>Payment date</Label>
                <Input type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} data-testid={`paid-date-${payout.payout_id}`} />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <Upload className="w-3.5 h-3.5" /> Proof of payment (optional)
                </Label>
                <Input
                  type="file"
                  accept="image/png,image/jpeg,application/pdf"
                  onChange={(e) => setPaidProofFile(e.target.files?.[0] || null)}
                  data-testid={`paid-proof-${payout.payout_id}`}
                  className="cursor-pointer"
                />
                {paidProofFile && (
                  <p className="text-[10px] text-stone-500 mt-1 truncate">
                    Attached: <span className="font-mono">{paidProofFile.name}</span> ({Math.round(paidProofFile.size / 1024)} KB)
                  </p>
                )}
                <p className="text-[10px] text-stone-400 mt-1">PNG, JPEG or PDF · max 5 MB</p>
              </div>
            </div>
          ) : (
            <div className="mt-2">
              <Label>{actionDialog?.action === 'reject' ? 'Reason' : actionDialog?.action === 'needs-more-info' ? 'What do you need?' : 'Hold reason'}</Label>
              <Textarea
                value={actionText}
                onChange={(e) => setActionText(e.target.value)}
                rows={3}
                placeholder="Required"
                data-testid={`action-text-${payout.payout_id}`}
              />
            </div>
          )}
          <DialogFooter className="mt-3 gap-2">
            <Button variant="outline" onClick={() => setActionDialog(null)}>Cancel</Button>
            <Button onClick={submitAction} disabled={actionSaving} className="bg-[#1F3D2B] hover:bg-[#1F3D2B]/90 text-white" data-testid={`submit-action-${payout.payout_id}`}>
              {actionSaving ? 'Saving…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Banking reveal confirmation */}
      <Dialog open={revealConfirm} onOpenChange={setRevealConfirm}>
        <DialogContent data-testid={`reveal-confirm-${payout.payout_id}`}>
          <DialogHeader>
            <DialogTitle className="text-rose-700 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" /> Reveal organiser banking details
            </DialogTitle>
            <DialogDescription>
              You are about to reveal the organiser&rsquo;s full account number. This action is audit-logged and visible to other admins. Only proceed if you are processing this payout right now.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2 gap-2">
            <Button variant="outline" onClick={() => setRevealConfirm(false)}>Cancel</Button>
            <Button onClick={reveal} disabled={revealing} className="bg-rose-700 hover:bg-rose-800 text-white" data-testid={`reveal-confirm-btn-${payout.payout_id}`}>
              {revealing ? 'Revealing…' : 'I understand — reveal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revealed banking display (post-confirmation, this view only) */}
      {revealed && (
        <div className="mt-3 bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs space-y-1" data-testid={`revealed-banking-${payout.payout_id}`}>
          <p className="font-semibold text-rose-900 flex items-center gap-1.5">
            <ShieldAlert className="w-3.5 h-3.5" /> Banking details revealed (this view only)
          </p>
          {revealed.is_live_fallback && (
            <p
              className="text-[11px] text-amber-900 bg-amber-100 border border-amber-200 rounded-md px-2 py-1"
              data-testid={`revealed-banking-fallback-${payout.payout_id}`}
            >
              Note: showing the organiser&rsquo;s CURRENT profile banking — no snapshot was captured for this payout. These details may differ from what was on file when the payout was first requested.
            </p>
          )}
          <p>Holder: <strong>{revealed.account_holder_name}</strong></p>
          <p>Bank: <strong>{revealed.bank_name}</strong> · Branch: <strong className="font-mono">{revealed.branch_code}</strong></p>
          <p>Type: <strong>{(revealed.account_type || '').toUpperCase()}</strong></p>
          <p>Account number: <strong className="font-mono">{revealed.account_number}</strong></p>
        </div>
      )}


      {/* Inline breakdown — collapsible */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="mt-3 text-xs text-[#1F3D2B] inline-flex items-center gap-1 hover:underline"
        data-testid={`toggle-breakdown-${payout.payout_id}`}
      >
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        {open ? 'Hide' : 'Show'} payout breakdown
      </button>

      {open && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 bg-stone-50 border border-stone-200 rounded-xl p-4" data-testid={`breakdown-${payout.payout_id}`}>
          {/* Totals */}
          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5">Settlement totals</p>
            <Row label="Gross sales" value={totals.gross || 0} />
            <Row label="VinePass fees" value={totals.fees || 0} negative />
            {totals.payfast_fees ? <Row label="PayFast fees" value={totals.payfast_fees} negative /> : null}
            <Row label="Refunds" value={payout.refunds || 0} negative />
            <Row label="Chargebacks" value={payout.chargebacks || 0} negative />
            <Row label="Reserve holds" value={payout.reserve_holds || 0} negative />
            <div className="border-t border-stone-300 pt-1.5 mt-1.5">
              <Row label="Net payout requested" value={payout.amount || 0} emphasis />
            </div>

            {editable && !editing && (
              <button
                onClick={() => setEditing(true)}
                className="mt-3 text-xs text-[#1F3D2B] inline-flex items-center gap-1 hover:underline"
                data-testid={`edit-adjustments-${payout.payout_id}`}
              >
                <Pencil className="w-3 h-3" /> Adjust chargebacks / reserve holds
              </button>
            )}
            {editable && editing && (
              <div className="mt-3 space-y-2 border-t border-stone-300 pt-3">
                <p className="text-[10px] uppercase tracking-wider text-stone-500">Manual adjustments</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px]">Chargebacks (R)</Label>
                    <Input value={cb} onChange={(e) => setCb(e.target.value)} type="number" min="0" step="0.01" data-testid={`cb-input-${payout.payout_id}`} />
                  </div>
                  <div>
                    <Label className="text-[10px]">Reserve holds (R)</Label>
                    <Input value={rh} onChange={(e) => setRh(e.target.value)} type="number" min="0" step="0.01" data-testid={`rh-input-${payout.payout_id}`} />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px]">Note (optional)</Label>
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this adjustment?" />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                  <Button size="sm" onClick={save} disabled={saving} className="rounded-full bg-[#1F3D2B] text-white hover:bg-[#172E20]" data-testid={`save-adjustments-${payout.payout_id}`}>
                    {saving ? 'Saving…' : 'Save adjustments'}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Per-event rows */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5">Per-event audit</p>
            {rows.length === 0 ? (
              <p className="text-xs text-stone-400">No past-event rows captured.</p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {rows.map((r) => (
                  <div key={r.event_id} className="text-xs border-b border-stone-200 pb-1.5">
                    <p className="font-medium text-[#1F3D2B] truncate">{r.title}</p>
                    <div className="flex justify-between text-stone-600 mt-0.5">
                      <span>{r.tickets} ticket{r.tickets === 1 ? '' : 's'} · {r.date}</span>
                      <span className="font-mono">R{(r.net || 0).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminPayoutsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/payouts`, { withCredentials: true });
      setPayouts(res.data?.payouts || []);
    } catch {
      toast.error('Could not load payouts.');
    } finally {
      setLoading(false);
    }
  };

  const decide = async (id, action) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/payouts/${id}/${action}`, {}, { withCredentials: true });
      toast.success(`Payout ${action.replace('-', ' ')}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update.');
    }
  };

  // Phase 3 — generic state-transition with a body (reject / mark-paid /
  // needs-more-info / on-hold). Backend enforces the per-action validation.
  const action = async (id, act, body) => {
    await axios.post(`${BACKEND_URL}/api/admin/payouts/${id}/${act}`, body || {}, { withCredentials: true });
    toast.success(`Payout ${act.replace('-', ' ')}`);
    fetchAll();
  };

  const uploadProof = async (id, dataUrl, filename) => {
    const res = await axios.post(
      `${BACKEND_URL}/api/admin/payouts/${id}/proof/upload`,
      { data_url: dataUrl, filename },
      { withCredentials: true },
    );
    fetchAll();
    return res.data;
  };

  const revealBanking = async (id) => {
    const res = await axios.post(
      `${BACKEND_URL}/api/admin/payouts/${id}/banking/reveal`,
      {},
      { withCredentials: true },
    );
    return res.data;
  };

  const adjust = async (id, body) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/payouts/${id}/adjust`, body, { withCredentials: true });
      toast.success('Adjustments saved');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save adjustments.');
    }
  };

  if (loading || authLoading) return <div className="min-h-[60vh] flex items-center justify-center text-[#1F3D2B]">Loading…</div>;

  const pendingCount = payouts.filter((p) => p.status === 'pending').length;

  return (
    <div className="min-h-screen bg-[#FAFAF7] py-12 px-4" data-testid="admin-payouts-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Wallet className="w-6 h-6 text-[#1F3D2B]" />
          <h1 className="font-serif text-3xl text-[#1F3D2B]">Payout requests</h1>
        </div>
        <p className="text-stone-500 mb-8">{pendingCount} pending across the platform.</p>

        <div className="bg-white border border-[#E5E9E6] rounded-xl shadow-sm overflow-hidden" data-testid="admin-payouts-table">
          {payouts.length === 0 ? (
            <div className="p-12 text-center text-stone-500">No payout requests yet.</div>
          ) : (
            <div className="divide-y divide-[#E5E9E6]">
              {payouts.map((p) => (
                <PayoutCard
                  key={p.payout_id}
                  payout={p}
                  onDecide={decide}
                  onAdjust={adjust}
                  onAction={action}
                  onUploadProof={uploadProof}
                  onRevealBanking={revealBanking}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPayoutsPage;
